import React, { createContext, ReactNode, useState } from 'react';
import { DialogProps } from '@mui/material';

/** 'destructive' keeps the red confirm on the left; 'positive' follows the usual convention. */
export type ConfirmTone = 'destructive' | 'positive';

export type ConfirmOptions = {
  tone: ConfirmTone;
  maxWidth: DialogProps['maxWidth'];
};

export const defaultConfirmOptions: ConfirmOptions = { tone: 'destructive', maxWidth: 'sm' };

interface Context {
  open: boolean;
  texts: Texts;
  handlers: Handlers;
  options: ConfirmOptions;
  setOptions: (options: ConfirmOptions) => void;
  setOpen: (open: boolean) => void;
  setTexts: (
    title: string,
    description: ReactNode,
    cancelText: string,
    confirmText: string,
    loadingText: string
  ) => void;
  setHandlers: (onConfirm: () => Promise<unknown>, onCancel: () => Promise<unknown>) => void;
}

interface Texts {
  title: string;
  description: ReactNode;
  cancelText: string;
  confirmText: string;
  loadingText: string;
}

interface Handlers {
  onConfirm: () => Promise<unknown>;
  onCancel: () => Promise<unknown>;
}

const defaultHandlers = {
  onConfirm: async () => undefined,
  onCancel: async () => undefined,
};

const defaultTexts = {
  title: '',
  description: '',
  cancelText: '',
  confirmText: '',
  loadingText: '',
};

const contextDefaultValues: Context = {
  open: false,
  texts: defaultTexts,
  handlers: defaultHandlers,
  options: defaultConfirmOptions,
  setOptions: () => undefined,
  setOpen: () => undefined,
  setTexts: () => undefined,
  setHandlers: () => undefined,
};

export const ConfirmContext = createContext<Context>(contextDefaultValues);

export const ConfirmContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [texts, setTexts] = useState<Texts>(defaultTexts);
  const [handlers, setHandlers] = useState<Handlers>(defaultHandlers);
  const [options, setOptions] = useState<ConfirmOptions>(defaultConfirmOptions);

  return (
    <ConfirmContext.Provider
      value={{
        open,
        texts,
        handlers,
        options,
        setOptions,
        setOpen,
        setTexts: (title, description, cancelText, confirmText, loadingText) =>
          setTexts({ title, description, cancelText, confirmText, loadingText }),
        setHandlers: (onConfirm, onCancel) => setHandlers({ onConfirm, onCancel }),
      }}
    >
      {children}
    </ConfirmContext.Provider>
  );
};
